import { useState } from "react";
import {
  isJacksonMetadata,
  resolveValueByPath,
} from "../../../utils/historyLogUtils";
import "./DifferenceDisplay.scss";

/**
 * Determine the change type based on from/to values.
 * @returns {"added"|"removed"|"modified"}
 */
const getChangeType = (from, to) => {
  if (from === null || from === undefined) return "added";
  if (to === null || to === undefined) return "removed";
  return "modified";
};

/**
 * Component to display the changes between previous and current values in history logs
 * @param {Object} props - Component props
 * @param {Array} props.differences - Array of difference objects
 * @param {Object} [props.entityData] - Parsed entity data used to resolve actual values
 */
export function DifferenceDisplay({ differences, entityData }) {
  const [expandedFields, setExpandedFields] = useState({});

  if (!differences || differences.length === 0) {
    return <p>No differences found</p>;
  }

  // Helper function to format field names from dot-notation or snake_case to readable form
  const formatFieldName = (fieldName) => {
    // Take the last segment of a dot-separated path for display
    const lastSegment = fieldName.includes(".")
      ? fieldName.split(".").pop()
      : fieldName;

    return lastSegment
      .split("_")
      .map((part) => part.replace(/([a-z])([A-Z])/g, "$1 $2"))
      .join(" ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Format the full field path for display in the expanded section
  const formatFieldPath = (fieldName) => {
    return fieldName
      .split(".")
      .map((segment) =>
        segment
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ")
      )
      .join(" → ");
  };

  // Toggle the expanded state for a specific field
  const toggleExpand = (fieldName) => {
    setExpandedFields((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  // Try to parse JSON string, return original if not valid JSON
  const tryParseJson = (jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return jsonString;
    }
  };

  // Format value for display
  const formatValue = (value) => {
    if (value === undefined || value === null) {
      return "";
    }

    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  /**
   * Resolve the display value for a diff side.
   * If the raw value is Jackson metadata, try to look up the real value
   * from entityData using the field path.
   */
  const resolveDisplayValue = (parsedValue, fieldPath) => {
    if (!isJacksonMetadata(parsedValue)) {
      return formatValue(parsedValue);
    }

    // Try to resolve actual value from entityData
    if (entityData && fieldPath) {
      const resolved = resolveValueByPath(entityData, fieldPath);
      if (resolved !== undefined) {
        return formatValue(resolved);
      }
    }

    return "-";
  };

  const changeTypeLabels = {
    added: "Added",
    removed: "Removed",
    modified: "Modified",
  };

  return (
    <div className="difference-display">
      <h4>Changes Made</h4>
      {differences.map((diff, index) => {
        const toValue =
          typeof diff.to === "string" ? tryParseJson(diff.to) : diff.to;

        const changeType = getChangeType(diff.from, diff.to);
        const fieldPath = diff.field?.includes(".")
          ? formatFieldPath(diff.field)
          : null;

        return (
          <div key={index} className="difference-item">
            <button
              className="difference-header"
              onClick={() => toggleExpand(diff.field)}
              type="button"
            >
              <div className="header-left">
                <span className="field-name">{formatFieldName(diff.field)}</span>
                <span
                  className={`change-badge change-badge--${changeType}`}
                  data-testid="change-badge"
                >
                  {changeTypeLabels[changeType]}
                </span>
              </div>
              <span className="expand-icon">
                {expandedFields[diff.field] ? "▼" : "▶"}
              </span>
            </button>

            {expandedFields[diff.field] && (
              <div className="difference-content" data-testid="difference-content">
                {fieldPath && (
                  <div className="field-path" data-testid="field-path">
                    {fieldPath}
                  </div>
                )}
                <div className="value-box">
                  <div className="value-label">Current Value</div>
                  <pre data-testid="to-pre">
                    {changeType === "removed"
                      ? "Not set"
                      : resolveDisplayValue(toValue, diff.field)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
